import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DatabaseService } from '../../services/DatabaseService';
import { QRCodeCanvas } from 'qrcode.react';
import VotingInterface from './VotingInterface';
import ResultsView from './ResultsView';
import ResultsAnimation from './ResultsAnimation';
import VotingNotification from './VotingNotification';

const LobbyView = ({ poll, isHost, participantId }) => {
  const [participants, setParticipants] = useState([]);
  const [lobbyStatus, setLobbyStatus] = useState('waiting'); // 'waiting', 'voting', 'results'
  const [showAnimation, setShowAnimation] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [showVotingNotification, setShowVotingNotification] = useState(false);
  const [prevLobbyStatus, setPrevLobbyStatus] = useState('waiting');
  
  const { pollId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const votingInterfaceRef = useRef(null);
  
  const pollUrl = `${window.location.origin}/polls/${pollId}/vote`;
  
  // Listen for real-time updates to the lobby
  useEffect(() => {
    if (!poll) return;
    
    const unsubscribe = DatabaseService.subscribeToPollParticipants(
      pollId,
      (updatedParticipants) => {
        setParticipants(updatedParticipants);
      },
      (updatedStatus) => {
        setPrevLobbyStatus(lobbyStatus);
        setLobbyStatus(updatedStatus);
        
        // If status just changed to voting and user is not host, show notification
        if (updatedStatus === 'voting' && lobbyStatus !== 'voting' && !isHost) {
          setShowVotingNotification(true);
        }
        
        if (updatedStatus === 'completed' && !isHost) {
          setShowAnimation(true);
        }
      },
      (error) => {
        setError(`Error listening to updates: ${error.message}`);
      }
    );
    
    // Clean up listener on component unmount
    return () => unsubscribe();
  }, [poll, pollId, isHost, lobbyStatus]);
  
  // Scroll to voting interface when voting starts
  useEffect(() => {
    if (lobbyStatus === 'voting' && prevLobbyStatus !== 'voting' && !isHost && votingInterfaceRef.current) {
      // Small delay to ensure the element is rendered
      setTimeout(() => {
        votingInterfaceRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 500);
    }
  }, [lobbyStatus, prevLobbyStatus, isHost]);
  
  const copyPollLink = () => {
    navigator.clipboard.writeText(pollUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        setError(`Error copying link: ${err.message}`);
      });
  };
  
  const startVoting = async () => {
    if (!isHost) return;
    
    try {
      await DatabaseService.updatePollStatus(pollId, 'voting');
      setLobbyStatus('voting');
    } catch (err) {
      setError(`Failed to start voting: ${err.message}`);
    }
  };
  
  const endVoting = async () => {
    if (!isHost) return;
    
    try {
      await DatabaseService.updatePollStatus(pollId, 'completed');
      setLobbyStatus('results');
      setShowAnimation(true);
    } catch (err) {
      setError(`Failed to end voting: ${err.message}`);
    }
  };
  
  const closeAnimation = () => {
    setShowAnimation(false);
  };
  
  if (!poll) {
    return <div>Loading...</div>;
  }
  
  // Render based on the lobby status
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Voting Notification */}
      <VotingNotification 
        show={showVotingNotification} 
        onClose={() => setShowVotingNotification(false)} 
      />
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {/* Poll Header */}
      <div className="border-b border-gray-200 pb-5 mb-5">
        <h1 className="text-3xl font-bold text-gray-900">{poll.title}</h1>
        {poll.category && (
          <div className="mt-1">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              {poll.category}
            </span>
          </div>
        )}
        {poll.description && (
          <p className="mt-2 text-sm text-gray-500">{poll.description}</p>
        )}
      </div>
      
      {/* QR Code and Joining Info */}
      <div className="mb-8 p-6 bg-white shadow-lg rounded-lg">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Join this poll</h2>
            <p className="text-gray-600 mb-4">
              Use the QR code or link below to join this poll:
            </p>
            <div className="flex items-center mt-2">
              <input 
                type="text"
                readOnly
                value={pollUrl}
                className="flex-1 min-w-0 block w-full px-3 py-2 rounded-md border border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
              />
              <button
                onClick={copyPollLink}
                className="ml-3 inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          <div className="bg-white p-3 border rounded-md">
            <QRCodeCanvas value={pollUrl} size={150} />
          </div>
        </div>
      </div>
      
      {/* Participant List */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Participants ({participants.length})
          </h2>
          {participants.length > 0 && lobbyStatus === 'waiting' && isHost && (
            <button
              onClick={startVoting}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Start Voting
            </button>
          )}
          {lobbyStatus === 'voting' && isHost && (
            <button
              onClick={endVoting}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              End Voting & Show Results
            </button>
          )}
        </div>
        
        <div className="bg-gray-50 rounded-lg border overflow-hidden">
          {participants.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <p>No participants have joined yet.</p>
              <p className="mt-2 text-sm">Share the link or QR code to invite people!</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {participants.map((participant, index) => (
                <li key={participant.id} className="px-6 py-4 flex items-center">
                  <div className="w-8 h-8 bg-indigo-100 text-indigo-800 rounded-full flex items-center justify-center mr-3">
                    {participant.name ? participant.name.charAt(0).toUpperCase() : '#'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {participant.name || `Participant ${index + 1}`}
                    </p>
                    {participant.hasVoted && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Voted
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">
                    Joined {new Date(participant.joinedAt).toLocaleTimeString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className={`p-6 border rounded-lg text-center ${
          lobbyStatus === 'waiting' ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="font-medium mb-2">Waiting for Participants</div>
          <div className="text-sm">
            {lobbyStatus === 'waiting' ? 'Current Phase' : 'Completed'}
          </div>
        </div>
        
        <div className={`p-6 border rounded-lg text-center ${
          lobbyStatus === 'voting' ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="font-medium mb-2">Voting Phase</div>
          <div className="text-sm">
            {lobbyStatus === 'waiting' ? 'Coming Up' : 
             lobbyStatus === 'voting' ? 'Current Phase' : 'Completed'}
          </div>
        </div>
        
        <div className={`p-6 border rounded-lg text-center ${
          lobbyStatus === 'results' ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="font-medium mb-2">Results</div>
          <div className="text-sm">
            {lobbyStatus === 'results' ? 'Current Phase' : 'Coming Up'}
          </div>
        </div>
      </div>
      
      {/* Vote Interface or Results */}
      {lobbyStatus === 'voting' && !isHost && (
        <div 
          ref={votingInterfaceRef} 
          className={`bg-white shadow-lg rounded-lg p-6 ${
            prevLobbyStatus !== 'voting' ? 'animate-pulse-gentle' : ''
          }`}
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Cast Your Vote</h2>
          <VotingInterface poll={poll} pollId={pollId} participantId={participantId} />
        </div>
      )}
      
      {lobbyStatus === 'results' && (
        <div className="bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Results</h2>
          <ResultsView pollId={pollId} />
        </div>
      )}
      
      {/* Results Animation */}
      {showAnimation && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-screen overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Vote Calculation Animation</h2>
              <button
                onClick={closeAnimation}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ResultsAnimation pollId={pollId} onFinish={closeAnimation} />
          </div>
        </div>
      )}
    </div>
  );
};

export default LobbyView;